import { sanitizeRichHtml } from "@lib/sanitizeHtml"
import { useEffect } from "react"
import "./styles.css"
import type { ShipProps, SpriteFrame } from "@site-types/components/ship"

const spriteFrames: SpriteFrame[] = ["l45", "l30", "l15", "m", "r15", "r30", "r45"]

export function Ship({ controls, ammoLaser, ammoSpread, ammoBomb, ammoRapid }: ShipProps) {
  useEffect(() => {
    const ship = document.querySelector<HTMLElement>("#ship")
    const shipSprite = document.querySelector<HTMLElement>(".ship__sprite")
    const bulletsContainer = document.querySelector<HTMLElement>("#bullets")
    const controlsHint = document.querySelector<HTMLElement>("#controls-hint")
    const ammoHud = document.querySelector<HTMLElement>("#ammo-hud")
    const fireHud = document.querySelector<HTMLElement>("#fire-hud")
    const ammoLabel = document.querySelector<HTMLElement>("#ammo-label")
    const ammoDots = document.querySelectorAll<HTMLElement>(".ammo-dot")
    const ammoPrev = document.querySelector<HTMLElement>("#ammo-prev")
    const ammoNext = document.querySelector<HTMLElement>("#ammo-next")
    const fireButton = document.querySelector<HTMLElement>("#fire-btn")
    const joystick = document.querySelector<HTMLElement>("#mobile-joystick")
    const joystickThumb = document.querySelector<HTMLElement>("#joystick-thumb")

    const ammoTypes = [
      { id: "laser", label: ammoLaser, color: "#00e5ff", cooldown: 250 },
      { id: "spread", label: ammoSpread, color: "#00ff88", cooldown: 450 },
      { id: "bomb", label: ammoBomb, color: "#ff8800", cooldown: 900 },
      { id: "rapid-laser", label: ammoRapid, color: "#cc44ff", cooldown: 80 },
    ] as const

    let animationFrame = 0
    let collisionFrame = 0
    let placeBackTimer = 0
    let controlsShowTimer = 0
    let controlsHideTimer = 0
    let hudTimer = 0
    let fireHoldTimer = 0
    let shipX = 0
    let targetShipX = 0
    let mouseTargetX = 0
    let keyboardOffsetX = 0
    let shipAngle = 0
    let lastAnimationMs = 0
    let lastCollisionMs = 0
    let lastFiredAt = 0
    let currentAmmoIndex = 0
    let joystickActive = false
    let joystickPointerId = -1
    const keyState: Record<string, boolean> = {}

    function updateAmmoHud() {
      const ammo = ammoTypes[currentAmmoIndex]
      if (ammoLabel) {
        ammoLabel.textContent = ammo.label
        ammoLabel.style.color = ammo.color
      }
      ammoDots.forEach((dot, index) => {
        const active = index === currentAmmoIndex
        dot.style.background = active ? ammo.color : "rgba(255,255,255,0.2)"
        dot.style.color = active ? ammo.color : "transparent"
        dot.classList.toggle("active", active)
      })
    }

    function cycleAmmo(direction: 1 | -1) {
      currentAmmoIndex = (currentAmmoIndex + direction + ammoTypes.length) % ammoTypes.length
      updateAmmoHud()
    }

    function spawnProjectile(viewportX: number, startY: number, cssClass: string, durationMs: number) {
      if (!bulletsContainer) return
      const projectile = document.createElement("div")
      projectile.className = cssClass
      projectile.dataset.type = cssClass
      projectile.dataset.startY = String(startY)
      projectile.style.left = `${viewportX - 50}px`
      bulletsContainer.appendChild(projectile)
      const animation = projectile.animate([{ top: `${startY}px` }, { top: "-40px" }], {
        duration: durationMs,
        easing: "linear",
        fill: "forwards",
      })
      animation.onfinish = () => projectile.remove()
    }

    function shoot() {
      if (!shipSprite) return
      const now = performance.now()
      const ammo = ammoTypes[currentAmmoIndex]
      if (now - lastFiredAt < ammo.cooldown) return
      lastFiredAt = now

      const rect = shipSprite.getBoundingClientRect()
      const centerX = rect.left + rect.width / 2
      const topY = rect.top

      if (ammo.id === "spread") {
        const offset = rect.width / 2 - 6
        spawnProjectile(centerX - offset, topY, "laser", 520)
        spawnProjectile(centerX, topY, "laser", 490)
        spawnProjectile(centerX + offset, topY, "laser", 520)
        return
      }

      spawnProjectile(
        centerX,
        topY,
        ammo.id,
        ammo.id === "bomb" ? 1100 : ammo.id === "rapid-laser" ? 260 : 500
      )
    }

    function spawnImpact(x: number, y: number) {
      const impact = document.createElement("div")
      impact.className = "ship-impact"
      impact.style.left = `${x}px`
      impact.style.top = `${y}px`
      document.body.appendChild(impact)
      const animation = impact.animate(
        [
          { scale: "1", opacity: "1" },
          { scale: "3", opacity: "0" },
        ],
        {
          duration: 300,
          easing: "ease-out",
          fill: "forwards",
        }
      )
      animation.onfinish = () => impact.remove()
    }

    function collisionLoop(timestamp: number) {
      collisionFrame = requestAnimationFrame(collisionLoop)
      if (timestamp - lastCollisionMs < 1000 / 60 || !bulletsContainer) return
      lastCollisionMs = timestamp

      for (const event of document.querySelectorAll<HTMLElement>(".event")) {
        const eventRect = event.getBoundingClientRect()
        for (const bullet of Array.from(bulletsContainer.children) as HTMLElement[]) {
          const bulletRect = bullet.getBoundingClientRect()
          const startY = Number(bullet.dataset.startY)
          if (bulletRect.top > startY - 80) continue
          if (bullet.dataset.hit) continue
          const colliding = !(
            eventRect.right < bulletRect.left ||
            eventRect.left > bulletRect.right ||
            eventRect.bottom < bulletRect.top ||
            eventRect.top > bulletRect.bottom
          )
          if (!colliding) continue

          bullet.dataset.hit = "1"
          spawnImpact(bulletRect.left + bulletRect.width / 2, bulletRect.top + bulletRect.height / 2)
          bullet.animate([{ opacity: "1" }, { opacity: "0" }], {
            duration: 150,
            easing: "ease-out",
            fill: "forwards",
          })
          event.classList.remove("hit-shake")
          void event.offsetWidth
          event.classList.add("hit-shake")
          break
        }
      }
    }

    function animationLoop(timestamp: number) {
      animationFrame = requestAnimationFrame(animationLoop)
      if (timestamp - lastAnimationMs < 1000 / 60) return
      lastAnimationMs = timestamp

      if (keyState.ArrowLeft) keyboardOffsetX -= 8
      else if (keyState.ArrowRight) keyboardOffsetX += 8
      else keyboardOffsetX *= 0.85

      const halfPage = (document.body.clientWidth - 50) / 2 - 60
      targetShipX = Math.min(Math.max(mouseTargetX + keyboardOffsetX, -halfPage), halfPage)
      const nextShipX = (shipX * 5 + targetShipX) / 6
      const delta = nextShipX - shipX
      shipX = nextShipX
      shipAngle = delta / 5 + (shipAngle * 9) / 10
      const spriteIndex = Math.round(Math.min(Math.max(shipAngle, -3), 3)) + 3

      ship?.style.setProperty("--ship-x", `${shipX}px`)
      if (shipSprite) {
        shipSprite.classList.remove(...spriteFrames)
        shipSprite.classList.add(spriteFrames[spriteIndex])
      }
    }

    function onMouseMove(event: MouseEvent) {
      mouseTargetX = event.clientX - document.body.clientWidth / 2
    }

    function onMouseClick(event: MouseEvent) {
      if ((event as PointerEvent).pointerType === "touch") return
      shoot()
    }

    function onKeyDown(event: KeyboardEvent) {
      if ([" ", "ArrowLeft", "ArrowRight"].includes(event.key)) event.preventDefault()
      keyState[event.key] = true
      if (event.key === " ") shoot()
      if (event.key === "q" || event.key === "Q") cycleAmmo(-1)
      if (event.key === "e" || event.key === "E") cycleAmmo(1)
    }

    function onKeyUp(event: KeyboardEvent) {
      keyState[event.key] = false
    }

    function updateJoystick(clientX: number) {
      if (!joystick || !joystickThumb) return
      const radius = joystick.offsetWidth / 2
      const rect = joystick.getBoundingClientRect()
      const dx = Math.max(-radius, Math.min(radius, clientX - (rect.left + rect.width / 2)))
      joystickThumb.style.translate = `calc(-50% + ${dx}px) -50%`
      mouseTargetX = (dx / radius) * ((document.body.clientWidth - 50) / 2 - 60)
      keyboardOffsetX = 0
    }

    function onJoystickDown(event: PointerEvent) {
      event.stopPropagation()
      joystick?.setPointerCapture(event.pointerId)
      joystickActive = true
      joystickPointerId = event.pointerId
      if (joystickThumb) joystickThumb.style.transition = "none"
      updateJoystick(event.clientX)
    }

    function onJoystickMove(event: PointerEvent) {
      if (!joystickActive || event.pointerId !== joystickPointerId) return
      event.stopPropagation()
      updateJoystick(event.clientX)
    }

    function onJoystickUp(event: PointerEvent) {
      if (event.pointerId !== joystickPointerId) return
      event.stopPropagation()
      joystickActive = false
      joystickPointerId = -1
      if (joystickThumb) {
        joystickThumb.style.transition = "translate 0.12s ease"
        joystickThumb.style.translate = "-50% -50%"
      }
      mouseTargetX = shipX
      keyboardOffsetX = 0
    }

    function pulseFire() {
      if (!fireButton) return
      fireButton.classList.remove("firing")
      void fireButton.offsetWidth
      fireButton.classList.add("firing")
    }

    function onFireButtonDown(event: PointerEvent) {
      event.stopPropagation()
      shoot()
      pulseFire()
      window.clearInterval(fireHoldTimer)
      fireHoldTimer = window.setInterval(
        () => {
          shoot()
          pulseFire()
        },
        Math.max(ammoTypes[currentAmmoIndex].cooldown, 80)
      )
    }

    function onFireButtonUp(event: Event) {
      event.stopPropagation()
      window.clearInterval(fireHoldTimer)
    }

    function onAmmoPrev() {
      cycleAmmo(-1)
    }

    function onAmmoNext() {
      cycleAmmo(1)
    }

    ship?.classList.remove("place-back")
    updateAmmoHud()
    controlsShowTimer = window.setTimeout(() => controlsHint?.classList.add("visible"), 1200)
    controlsHideTimer = window.setTimeout(() => controlsHint?.classList.remove("visible"), 4000)
    hudTimer = window.setTimeout(() => {
      ammoHud?.classList.add("visible")
      fireHud?.classList.add("visible")
      joystick?.classList.add("visible")
    }, 1400)
    placeBackTimer = window.setTimeout(() => ship?.classList.add("place-back"), 50)

    animationFrame = requestAnimationFrame(animationLoop)
    collisionFrame = requestAnimationFrame(collisionLoop)
    document.addEventListener("mousemove", onMouseMove)
    document.addEventListener("keydown", onKeyDown)
    document.addEventListener("keyup", onKeyUp)
    document.addEventListener("click", onMouseClick)
    ammoPrev?.addEventListener("click", onAmmoPrev)
    ammoNext?.addEventListener("click", onAmmoNext)
    fireButton?.addEventListener("pointerdown", onFireButtonDown)
    fireButton?.addEventListener("pointerup", onFireButtonUp)
    fireButton?.addEventListener("pointerleave", onFireButtonUp)
    joystick?.addEventListener("pointerdown", onJoystickDown)
    joystick?.addEventListener("pointermove", onJoystickMove)
    joystick?.addEventListener("pointerup", onJoystickUp)
    joystick?.addEventListener("pointercancel", onJoystickUp)

    return () => {
      cancelAnimationFrame(animationFrame)
      cancelAnimationFrame(collisionFrame)
      window.clearTimeout(placeBackTimer)
      window.clearTimeout(controlsShowTimer)
      window.clearTimeout(controlsHideTimer)
      window.clearTimeout(hudTimer)
      window.clearInterval(fireHoldTimer)
      document.removeEventListener("mousemove", onMouseMove)
      document.removeEventListener("keydown", onKeyDown)
      document.removeEventListener("keyup", onKeyUp)
      document.removeEventListener("click", onMouseClick)
      ammoPrev?.removeEventListener("click", onAmmoPrev)
      ammoNext?.removeEventListener("click", onAmmoNext)
      fireButton?.removeEventListener("pointerdown", onFireButtonDown)
      fireButton?.removeEventListener("pointerup", onFireButtonUp)
      fireButton?.removeEventListener("pointerleave", onFireButtonUp)
      joystick?.removeEventListener("pointerdown", onJoystickDown)
      joystick?.removeEventListener("pointermove", onJoystickMove)
      joystick?.removeEventListener("pointerup", onJoystickUp)
      joystick?.removeEventListener("pointercancel", onJoystickUp)
      document.querySelectorAll(".ship-impact").forEach((impact) => impact.remove())
      bulletsContainer?.replaceChildren()
    }
  }, [ammoBomb, ammoLaser, ammoRapid, ammoSpread])

  return (
    <>
      <div className="shooter-container">
        <div
          className="ship"
          data-id="calendar-ship"
          id="ship"
        >
          <div
            className="ship__sprite m"
            data-id="calendar-ship-sprite"
          />
        </div>
        <div
          className="bullets"
          id="bullets"
        />
      </div>
      <div
        className="controls-hint"
        id="controls-hint"
        dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(controls) }}
      />
      <div
        className="ammo-hud"
        data-ammo-bomb={ammoBomb}
        data-ammo-laser={ammoLaser}
        data-ammo-rapid={ammoRapid}
        data-ammo-spread={ammoSpread}
        id="ammo-hud"
      >
        <div className="ammo-row">
          <span
            className="ammo-key"
            id="ammo-prev"
          >
            <span className="key-desktop">Q</span>
            <span className="key-mobile">←</span>
          </span>
          <span
            className="ammo-label"
            id="ammo-label"
          >
            {ammoLaser}
          </span>
          <span
            className="ammo-key"
            id="ammo-next"
          >
            <span className="key-desktop">E</span>
            <span className="key-mobile">→</span>
          </span>
        </div>
        <div
          className="ammo-dots"
          id="ammo-dots"
        >
          <span className="ammo-dot" />
          <span className="ammo-dot" />
          <span className="ammo-dot" />
          <span className="ammo-dot" />
        </div>
      </div>
      <div
        className="fire-hud"
        id="fire-hud"
      >
        <button
          className="fire-btn"
          id="fire-btn"
          type="button"
          aria-label="Fire"
        >
          ●
        </button>
      </div>
      <div
        className="mobile-joystick"
        id="mobile-joystick"
      >
        <div
          className="joystick-thumb"
          id="joystick-thumb"
        />
      </div>
    </>
  )
}

export default Ship
